/**
 * Hashes an email address using SHA-256 and returns a base64-encoded string.
 * This implementation matches the C# version in ProvetOnlineBooking.razor.cs
 *
 * @param email - The email address to hash
 * @returns A base64-encoded SHA-256 hash of the email
 */
export async function hashEmail(email: string): Promise<string> {
  try {
    if (typeof window !== "undefined" && window.crypto && window.crypto.subtle) {
      // Use Web Crypto API (modern browsers)
      const encoder = new TextEncoder();
      const data = encoder.encode(email);
      const hashBuffer = await window.crypto.subtle.digest("SHA-256", data);

      // Convert ArrayBuffer to base64 without using deprecated btoa()
      const hashArray = new Uint8Array(hashBuffer);
      return arrayBufferToBase64(hashArray);
    } else {
      // Fallback for older browsers or environments without Web Crypto API
      console.warn("Web Crypto API not available, email hashing unavailable");
      throw new Error("Web Crypto API not available");
    }
  } catch (error) {
    console.error("Error hashing email:", error);
    throw error;
  }
}

/**
 * Converts a Uint8Array to a base64 string without using deprecated btoa()
 * This uses the modern approach with base64 encoding characters
 *
 * @param buffer - The Uint8Array to convert
 * @returns A base64-encoded string
 */
function arrayBufferToBase64(buffer: Uint8Array): string {
  const base64Chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";
  let result = "";
  let i: number;

  // Process the buffer in chunks of 3 bytes
  for (i = 0; i < buffer.length - 2; i += 3) {
    // Combine 3 bytes into a 24-bit number
    const chunk = (buffer[i] << 16) | (buffer[i + 1] << 8) | buffer[i + 2];

    // Split into four 6-bit numbers and map to base64 characters
    result += base64Chars[(chunk >> 18) & 63];
    result += base64Chars[(chunk >> 12) & 63];
    result += base64Chars[(chunk >> 6) & 63];
    result += base64Chars[chunk & 63];
  }

  // Handle remaining bytes (if any)
  const remaining = buffer.length - i;
  if (remaining === 1) {
    const chunk = buffer[i] << 16;
    result += base64Chars[(chunk >> 18) & 63];
    result += base64Chars[(chunk >> 12) & 63];
    result += "==";
  } else if (remaining === 2) {
    const chunk = (buffer[i] << 16) | (buffer[i + 1] << 8);
    result += base64Chars[(chunk >> 18) & 63];
    result += base64Chars[(chunk >> 12) & 63];
    result += base64Chars[(chunk >> 6) & 63];
    result += "=";
  }

  return result;
}
